import type { ModuleId, RequiredLevel } from '@qlk/shared';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Bốn nhóm menu, theo thiết kế đã duyệt (xem `docs/ui.md`).
 *
 * Đổi so với bản đầu: `Kết quả` → `TÀI CHÍNH` (nhóm này giờ chỉ còn tiền), và
 * `Hiệu suất` chuyển sang `VẬN HÀNH` — người vận hành kênh xem số liệu hằng
 * ngày, còn `TÀI CHÍNH` là chỗ của kế toán.
 *
 * Viết hoa vì menu in hoa; đây cũng là **gốc breadcrumb** của trang.
 */
export const NHOM_MENU = ['VẬN HÀNH', 'NỘI DUNG', 'TÀI CHÍNH', 'QUẢN TRỊ'] as const;

export type NhomMenu = (typeof NHOM_MENU)[number];

export interface KhaiBaoNav {
  group: NhomMenu;
  label: string;
  icon: LucideIcon;
  /** Thứ tự trong nhóm, nhỏ hơn đứng trước. */
  order: number;
  /** Module cần quyền xem để hiện mục menu này. */
  permission: ModuleId;
  /**
   * Nhãn số nhỏ bên phải mục menu: `so` (xám, ví dụ số kênh) hoặc `nhap` (cam,
   * "N nháp" của kỳ đối soát). Giá trị do `Sidebar` lấy, khai báo chỉ nói
   * **loại** badge nào.
   */
  badge?: 'so-kenh' | 'doi-soat-nhap';
  /**
   * Mức quyền tối thiểu để **hiện** mục menu. Mặc định `view`.
   *
   * Cần cho màn hình chỉ quản trị mở được (Đối soát nhân sự): để mặc định thì
   * mục menu hiện với mọi người có `team:view` rồi route chặn 403 — người dùng
   * thấy một mục bấm vào là lỗi.
   */
  mucQuyen?: RequiredLevel;
  /**
   * Mục hiện với **mọi người đã đăng nhập**, không hỏi quyền module nào.
   *
   * Chỉ dùng cho trang mà server đã lọc theo chính người đang xem ("Thu nhập
   * của tôi" đọc `req.currentUser.id`). Trước 18/10/2026 mục đó khai
   * `permission: 'core'`, mà ma trận mặc định đặt `core = none` cho cả bốn vai
   * trò không phải quản trị — nên **không ai ngoài quản trị thấy mục tiền của
   * chính mình**, dù route mở.
   */
  luonHien?: boolean;
  /**
   * Hiện mục khi người dùng thoả **bất kỳ** quyền nào trong danh sách.
   *
   * Dành cho mục menu là **khung chứa** của nhiều module: "Cài đặt" gom mục cấu
   * hình của `team` (Công ty) và `sync` (Kết nối API), nên đòi `team:admin` là
   * ẩn nó khỏi người có `sync:edit` — đúng những người cần vào đó nhất, và
   * khung trống của trang Kênh thì lại chỉ họ "vào Cài đặt → Kết nối API".
   *
   * Là **hàm** chứ không phải mảng: danh sách suy ra lúc chạy từ các mục đã
   * đăng ký (`lib/caiDatTabs`), nên thêm một mục cấu hình mới không phải sửa
   * khai báo menu. Trả mảng rỗng ⇒ quay về `permission` + `mucQuyen`.
   */
  quyenBatKy?: () => { module: ModuleId; level: RequiredLevel }[];
  /** Đường dẫn của mục menu. Mặc định lấy route đầu tiên. */
  to?: string;
}

export interface KhaiBaoRoute {
  path: string;
  element: ReactNode;
  /**
   * Route công khai: render thẳng, **không** bọc `CanDangNhap` nên không dựng
   * `AppShell` và không gọi `/api/v1/core/me`. Mặc định `false`.
   * Hiện chỉ dùng cho trang đặt mật khẩu theo link mời của module 10.
   */
  congKhai?: boolean;
  /** Quyền tối thiểu để vào route. Không khai báo thì chỉ cần đăng nhập. */
  permission?: { module: ModuleId; level: RequiredLevel };
}

export interface KhaiBaoModule {
  id: ModuleId;
  nav?: KhaiBaoNav;
  /**
   * Mục menu **thêm** của cùng một module.
   *
   * `nav` ghép với menu chuẩn theo `permission` nên mỗi module chỉ có một mục ở
   * đó. Module nào có hai màn hình ngang hàng (module 10: "Nhân sự & phân
   * quyền" và "Nhóm & đội ngũ") thì khai mục thứ hai ở đây; chúng luôn hiện
   * đúng như khai báo, không ghép với menu chuẩn.
   */
  navThem?: KhaiBaoNav[];
  routes?: KhaiBaoRoute[];
}

/**
 * Nạp tự động mọi `src/modules/<id>/module.ts`.
 * Module mới chỉ cần thêm file, không phải sửa chỗ nào khác.
 */
const DA_NAP = import.meta.glob<{ default?: KhaiBaoModule } & Partial<KhaiBaoModule>>(
  '../modules/*/module.{ts,tsx}',
  { eager: true },
);

export const MODULE_DA_CAI: KhaiBaoModule[] = Object.values(DA_NAP)
  .map((m) => m.default ?? (m as KhaiBaoModule))
  .filter((m): m is KhaiBaoModule => Boolean(m.id));

export function layRouteCuaModule(): KhaiBaoRoute[] {
  return MODULE_DA_CAI.flatMap((m) => m.routes ?? []);
}
